<template>
  <div
    class="workspace-wrapper eg-tree"
    :data-testid="`workspace-wrapper-${workspaceKey}`"
  >
    <div
      class="workspace-header aling-items-center d-flex align-items-center mb-2"
    >
      <div class="d-flex align-items-center">
        <img
          v-if="workspaceIcon"
          :src="workspaceIcon"
          :alt="workspaceName"
          class="workspace-icon"
        />
        <span class="workspace-title caption-s">{{ workspaceName }}</span>
      </div>
      <div>
        <eg-button
          v-if="workspaceKey === 'essaygrader'"
          :icon-color="'black'"
          size="extra-small"
          icon="plus-add-more-maximize"
          class="ml-2 margin-right-5px"
          @click="handleAddFolder"
        />
        <folder-actions-dropdown
          v-else
          :workspace-key="workspaceKey"
          :is-workspace-actions="true"
          class="folder-actions-dropdown margin-right-6px"
        >
        </folder-actions-dropdown>
      </div>
    </div>

    <Tree
      :value="foldersNodes"
      selection-mode="single"
      :selection-keys="selectedFolder"
      :expanded-keys.sync="expandedKeys"
      @node-select="handleNodeClick"
    >
      <template #default="{ node }">
        <sidebar-workspace-item :item="node" :workspace-key="workspaceKey" />
      </template>
    </Tree>
  </div>
</template>

<script>
import Tree from "primevue/tree";

import CreateWorkspaceIntegrationModal from "@/views/dashboard/workspace-integration/modal/create-workspace-integration-modal.vue";
import WorkspaceSelectionModal from "@/views/dashboard/workspace-wizard/WorkspaceSelectionModal.vue";
import { capitalizeEachWord } from "@/utils/capitalize";
import { getIconPath as getImgPath } from "@/utils/getCorrectPath";
import { LMS_PROVIDERS, PLATFORMS } from "@/constants";
import { getWorkspaceProfileIdKey } from "@/utils/userUtils";
import { getFolderIcon } from "@/utils/getFolderIcon";
import SidebarWorkspaceItem from "@/components/global/sidebar/sidebar_workspace_item.vue";
import FolderActionsDropdown from "@/components/global/folder_actions_dropdown.vue";

export default {
  name: "SidebarWorkspace",

  components: {
    Tree,
    SidebarWorkspaceItem,
    FolderActionsDropdown,
  },

  inject: ["workspacesProvider"],

  props: {
    workspaceKey: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      selectedFolder: JSON.parse(localStorage.getItem("selectedFolder")) || {},
      expandedKeys: JSON.parse(localStorage.getItem("expandedKeys")) || {},
    };
  },

  computed: {
    folderIdFromRoute() {
      return this.$route?.params?.id;
    },

    foldersNodes() {
      const addKey = (nodes) => {
        return nodes.map((node) => {
          const newNode = {
            ...node,
            key: node.id,
            label: node.name,
            icon: getFolderIcon(node),
          };

          if (node.children && Array.isArray(node.children)) {
            newNode.children = addKey(node.children);
          }

          return newNode;
        });
      };

      return addKey(
        this.workspacesProvider.workspaces[this.workspaceKey]?.folders || []
      );
    },

    workspaceName() {
      switch (this.workspaceKey) {
        case PLATFORMS.ESSAYGRADER:
          return "EssayGrader";
        case PLATFORMS.GOOGLE:
          return "Google Classroom";
        default:
          return capitalizeEachWord(this.workspaceKey);
      }
    },

    workspaceIcon() {
      switch (this.workspaceKey) {
        case PLATFORMS.ESSAYGRADER:
          return getImgPath("essaygrader");
        case PLATFORMS.GOOGLE:
          return getImgPath("classroom");
        case PLATFORMS.CANVAS:
          return getImgPath("canvas");
        case PLATFORMS.SCHOOLOGY:
          return getImgPath("schoology");
        default:
          return null;
      }
    },
  },

  watch: {
    expandedKeys() {
      localStorage.setItem("expandedKeys", JSON.stringify(this.expandedKeys));
    },

    // Watch for route changes to clear selection when not on workspace routes
    "$route.path": {
      handler() {
        if (!this.$route?.name.startsWith("workspace")) {
          this.selectedFolder = {};
        }
      },
      immediate: true,
    },

    "$route.params.id": {
      handler() {
        if (!this.$route?.name.startsWith("workspace")) {
          this.selectedFolder = {};

          return;
        }

        const currentSelectedFolderId = Object.keys(this.selectedFolder)?.[0];

        if (+this.folderIdFromRoute === +currentSelectedFolderId) {
          return;
        }

        const currentFolder = this.workspacesProvider.getFolderById(
          this.workspaceKey,
          this.folderIdFromRoute
        );

        this.selectedFolder = {
          [this.folderIdFromRoute]: true,
        };

        if (!currentFolder) {
          return;
        }

        const ancestors = this.workspacesProvider.getFolderAncestors(
          this.workspaceKey,
          this.folderIdFromRoute
        );

        const newExpandedKeys = ancestors.reduce((acc, ancestor) => {
          acc[ancestor.id] = true;
          return acc;
        }, {});

        this.expandedKeys = {
          ...newExpandedKeys,
        };
      },
    },
  },

  methods: {
    handleNodeClick(folder) {
      const id = folder.id;

      if (!this.selectedFolder[id]) {
        if (
          !this.$route?.name.startsWith("workspace") ||
          (this.$route?.name.startsWith("workspace") &&
            +this.folderIdFromRoute !== +id)
        ) {
          this.navigateToFolder(id);
        }

        return;
      }

      this.navigateToFolder(id);
    },

    navigateToFolder(id) {
      const folderData = this.workspacesProvider.getFolderById(
        this.workspaceKey,
        id
      );

      this.$router
        .push({
          name:
            folderData.isAssignment && !folderData.hasGradingDetails
              ? "workspace-essay-upload"
              : "workspace",
          params: { workspaceKey: this.workspaceKey, id },
        })
        .then(() => {
          const newSelectedFolder = {
            [id]: true,
          };
          localStorage.setItem(
            "selectedFolder",
            JSON.stringify(newSelectedFolder)
          );
          const currentSelectedFolderId = Object.keys(this.selectedFolder)?.[0];

          if (+this.folderIdFromRoute === +currentSelectedFolderId) {
            return;
          }

          this.selectedFolder = newSelectedFolder;
        });
    },

    handleAddFolder() {
      if (this.workspaceKey === "essaygrader") {
        this.$egModal.show("add-eg-workspace-modal", {
          component: WorkspaceSelectionModal,
        });

        return;
      }

      let platformKey = this.workspaceKey;

      switch (this.workspaceKey) {
        case "google":
          platformKey = "googleClassroom";
          break;
        case "canvas":
          platformKey = "canvas";
          break;
        default:
          break;
      }

      this.createWorkspaceIntegration(platformKey);
    },
    createWorkspaceIntegration(platformKey) {
      const currentProfileId =
        this.$store.state.user[getWorkspaceProfileIdKey(platformKey)];

      if (currentProfileId) {
        this.navigateToImportAssignment(platformKey, currentProfileId);
        return;
      }

      this.$egModal.show("create-workspace-integration-modal", {
        component: CreateWorkspaceIntegrationModal,
        props: {
          platformKey: LMS_PROVIDERS[platformKey].key,
        },
        on: {
          onSuccess: (profileId) =>
            this.navigateToImportAssignment(platformKey, profileId),
        },
      });
    },
    navigateToImportAssignment(platformKey, profileId) {
      this.$router.push({
        name: "imports-assignments",
        params: {
          platformKey: platformKey,
          id: profileId,
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.workspace-wrapper {
  padding: 15px 0;
  margin: 0 20px;
  border-bottom: 1px solid var(--color-stroke-neutral-default);
  &:last-of-type {
    border-bottom: none;
  }
  & .workspace-icon {
    width: 20px;
    height: 20px;
    object-fit: contain;
    margin-right: 8px;
  }

  & .workspace-header {
    justify-content: space-between;
  }

  & .workspace-title {
    font-weight: var(--typography-font-weight-fw-600);
    color: var(--color-text-neutral-secondary);
  }
}

.margin-right-5px {
  margin-right: 5px;
}

.margin-right-6px {
  margin-right: 6px;
}

.folder-actions-dropdown::v-deep {
  .btn {
    padding: 0;
  }
}
/* Tree styles moved to global .eg-tree in assets/styles/tree-primevue.scss */
</style>
